import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, Download, ChevronDown, ChevronRight } from "lucide-react";
import Pager from "@/components/Pager";
import { downloadCsv } from "@/lib/csv";
import { dateTime } from "@/lib/pricing";

function MetaRow({ meta }) {
  const entries = Object.entries(meta || {});
  if (!entries.length) return <p className="text-xs text-slate-400 italic">No details recorded.</p>;
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 min-w-0">
          <dt className="text-slate-500 shrink-0">{k.replace(/_/g, " ")}</dt>
          <dd className="text-slate-800 font-mono break-all">{typeof v === "string" && !Number.isNaN(Date.parse(v)) && /\d{4}-\d{2}-\d{2}T/.test(v) ? dateTime(v) : typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1, page_size: 50 });
  const [open, setOpen] = useState(null);

  const load = (p = page) => {
    const qs = new URLSearchParams({ page: p, page_size: 50 });
    if (action) qs.set("action", action);
    if (actor) qs.set("actor", actor);
    api.get(`/admin/audit-logs?${qs}`).then((r) => {
      setItems(r.data.items || []);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages, page_size: r.data.page_size });
      setPage(r.data.page);
    });
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-audit-logs">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="pf-chip pf-chip-lav"><ShieldCheck className="w-3 h-3" /> Governance</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Audit log</h2>
          <p className="text-sm text-slate-600 mt-1">Append-only trail of every admin action — tier changes, suspensions, redemptions, ticket replies.</p>
        </div>
        <button
          onClick={() => {
            const qs = new URLSearchParams();
            if (action) qs.set("action", action);
            if (actor) qs.set("actor", actor);
            downloadCsv(`/admin/audit-logs.csv${qs.toString() ? `?${qs}` : ""}`, "cyannect-audit-logs.csv");
          }}
          className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
          data-testid="audit-export-csv"
        >
          <Download className="w-3 h-3" /> Download CSV
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(1); }} className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2 max-w-2xl">
        <input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action contains…" aria-label="Filter by action" className="pf-input px-3 py-2 text-sm" data-testid="audit-filter-action" />
        <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Actor email contains…" aria-label="Filter by actor email" className="pf-input px-3 py-2 text-sm" data-testid="audit-filter-actor" />
        <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="audit-search">Search</button>
      </form>

      <div className="pf-card overflow-x-auto mt-6">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3 whitespace-nowrap">When</th>
              <th className="px-5 py-3">Action</th>
              <th className="px-5 py-3">Actor</th>
              <th className="px-5 py-3">Target</th>
              <th className="px-5 py-3"><span className="sr-only">Details</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-t border-white/60" data-testid={`audit-${l.id}`}>
                  <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`audit-when-${l.id}`}>{dateTime(l.created_at)}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-800">{l.action}</td>
                  <td className="px-5 py-3 text-xs text-slate-700">{l.actor_email} <span className="text-slate-400">({l.actor_role})</span></td>
                  <td className="px-5 py-3 text-xs text-slate-500 font-mono" title={l.target_id || ""}>{l.target_type}/{(l.target_id || "").slice(0, 12)}{(l.target_id || "").length > 12 ? "…" : ""}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setOpen(open === l.id ? null : l.id)} aria-expanded={open === l.id} className="text-xs font-semibold text-cyan-700 hover:underline inline-flex items-center gap-1" data-testid={`audit-toggle-${l.id}`}>
                      {open === l.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Details
                    </button>
                  </td>
                </tr>
                {open === l.id && (
                  <tr className="bg-white/40" data-testid={`audit-details-${l.id}`}><td colSpan={5} className="px-5 py-3"><MetaRow meta={l.meta} /></td></tr>
                )}
              </Fragment>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">No audit events yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} totalPages={meta.total_pages} total={meta.total} pageSize={meta.page_size} onPage={load} testidPrefix="audit-pager" />
    </div>
  );
}
